import { enableProdMode } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { platformBrowser } from '@angular/platform-browser';

if (environment.production) {
  enableProdMode();
}

if (environment.sentry.enabled) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    release: environment.releaseVersion,
    integrations: [
      Sentry.browserSessionIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Optional privacy settings:
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracePropagationTargets: environment.sentry.tracingOrigins,
    ignoreErrors: [
      // Status 0 means the browser got no response: connection dropped, request cancelled by a
      // reload or navigation, or a proxy error without CORS headers. Nothing for us to act on.
      /Http failure response for .+: 0 Unknown Error/,
      // Status 401: the session expired. If the refresh is rejected, the user is logged out and
      // pending requests still fail with the original 401. Temporary refresh failures are
      // reported by AuthState.
      /Http failure response for .+: 401 /,
      /^Server returned code 401/,
    ],
    // Child spans dropped before sending. A pageload carries ~230 of them and almost all are
    // browser auto-instrumentation: one span per file fetched, per performance mark, per
    // navigation-timing step. The pageload and ui.interaction transactions stay, as do the
    // http.client spans to our own API, which are what links a trace to the backend.
    //
    // Web vitals are unaffected: lcp/cls/ttfb/fcp are measurements on the transaction, not spans.
    // Never match a root span op (pageload, navigation, ui.interaction.*) - that drops the whole
    // transaction.
    ignoreSpans: [
      { op: /^resource\./ },
      { op: /^browser\./ },
      { op: 'mark' },
      { op: 'measure' },
      { op: 'paint' },
      { op: 'ui.long-animation-frame' },
      { op: 'ui.long-task' },
      { name: /^Zone(:.*)?$/ },
      { name: /mark_feature_usage/ },
      // Our own bundled files, fetched by relative URL. Anchored, so calls to our API cannot
      // match - those are absolute (GET https://....menlo79.com/...).
      { name: /^GET \/assets\// },
      // Third parties we do not debug from traces. These match span descriptions, so they only
      // apply to http.client spans - every resource.* span is already dropped by op above. A
      // hostname that only ever serves scripts or assets does nothing here.
      { name: /app\.productfruits\.com/ },
      { name: /events\.launchdarkly\.com/ },
      { name: /app\.launchdarkly\.com/ },
      { name: /talkjs\.com/ },
      { name: /maps\.googleapis\.com/ },
      { name: /\.atlassian\.(net|com)/ },
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.2,
  });
}

platformBrowser()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));
